/**
 * Mike's Liquors — Keg Accessories Controller (Phase 1)
 *
 * Works with keg-accessories.liquid and the existing keg-deposit v2.1 module.
 * On a keg product page:
 *   1. Reads the customer's selections from the accessory panel.
 *   2. Computes a running add-on total (live).
 *   3. On "Add to Cart" click, posts a single /cart/add.js call with the keg
 *      + every selected add-on in items[]. Each add-on line carries a hidden
 *      "_For Keg" property so staff can match add-ons to parent kegs on
 *      multi-keg orders.
 *   4. Writes the "Returning Empty Kegs" count to cart attributes so the
 *      existing cart-page return panel (v2.1) picks it up.
 *
 * keg-deposit v2.1 will auto-add the $50 deposit when it sees the keg land
 * in the cart via the fetch wrapper — no action needed here.
 *
 * Variant ids come from window.MIKES_ADDONS (set in theme.liquid). Handles
 * that are missing are skipped gracefully with a console warning.
 */

(function mikesKegAccessories() {
  'use strict';

  var root = document.getElementById('mikes-keg-accessories');
  if (!root) return;
  if (!window.MIKES_ADDONS) {
    console.warn('[Keg Accessories] window.MIKES_ADDONS is missing — set it in theme.liquid.');
    return;
  }

  var kegVariantId = parseInt(root.dataset.kegVariant, 10);
  // Health-Ade / Stumptown kegs ship one-way (non-refundable) — no keg deposit.
  // Set in keg-accessories.liquid from NO_DEPOSIT_KEG_VENDORS.
  var kegNoDeposit = root.dataset.kegNoDeposit === 'true';
  var kegTitle = root.dataset.kegTitle || 'Keg';
  var couplerType = root.dataset.couplerType || 'Type D';
  var couplerHandleKey = couplerType.toLowerCase().replace(/[^a-z0-9]+/g, '-'); // "Type D" -> "type-d"

  // ── Gas picker wiring ─────────────────────────────────────────────
  // Each .mka-gas-picker (one per branch that offers gas: kegerator, jockey_box)
  // has three gas-type radios (none/co2/n2), a per-type size select, and a
  // shared qty input. Show the matching size + qty when a gas is picked,
  // hide them when "none". Variant IDs and prices are embedded in the
  // Liquid-rendered <option> tags, so we don't need MIKES_ADDONS for gas.
  root.querySelectorAll('.mka-gas-picker').forEach(function (picker) {
    var typeRadios = picker.querySelectorAll('.mka-gas-type');
    function updateVisibility() {
      var checked = picker.querySelector('.mka-gas-type:checked');
      var val = checked ? checked.value : 'none';
      picker.querySelectorAll('.mka-gas-size').forEach(function (el) {
        el.style.display = (el.getAttribute('data-gas') === val) ? '' : 'none';
      });
      var qtyDiv = picker.querySelector('.mka-gas-qty');
      if (qtyDiv) qtyDiv.style.display = (val === 'none') ? 'none' : '';
    }
    typeRadios.forEach(function (r) { r.addEventListener('change', updateVisibility); });
    updateVisibility();
  });

  /**
   * Read the gas-refill selection for a branch. Returns null if no gas is
   * selected. Shape matches buildAddons output so the caller can push into
   * lines[] directly. Each refill auto-triggers the $100 tank deposit via
   * deposits.js as soon as it lands in the cart — we don't add the deposit
   * here.
   */
  function readGasPicker(branch) {
    var picker = root.querySelector('.mka-gas-picker[data-branch="' + branch + '"]');
    if (!picker) return null;
    var typeInput = picker.querySelector('.mka-gas-type:checked');
    var type = typeInput ? typeInput.value : 'none';
    if (type === 'none') return null;
    var sizeDiv = picker.querySelector('.mka-gas-size[data-gas="' + type + '"]');
    if (!sizeDiv) return null;
    var sel = sizeDiv.querySelector('.mka-gas-variant');
    if (!sel) return null;
    var variantId = parseInt(sel.value, 10);
    if (isNaN(variantId)) return null;
    var unitPrice = parseFloat(
      sel.options[sel.selectedIndex] && sel.options[sel.selectedIndex].getAttribute('data-price')
    ) || 0;
    var qtyInput = picker.querySelector('.mka-gas-count');
    var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    if (isNaN(qty) || qty < 1) return null;
    if (qty > 999) qty = 999;
    var sizeLabel = sel.options[sel.selectedIndex] ? sel.options[sel.selectedIndex].text.split(' — ')[0] : '';
    var gasName = type === 'co2' ? 'CO₂' : 'N₂';
    return {
      variantId: variantId,
      quantity: qty,
      price: unitPrice * qty,
      label: qty + ' × ' + gasName + ' refill (' + sizeLabel + ')',
      gasType: type, // 'co2' | 'n2' — used by atomic ATC to inject the matching tank deposit in the same /cart/add.js
    };
  }

  function $(sel) { return root.querySelector(sel); }
  function val(sel) { var el = $(sel); return el ? el.value : null; }
  // Clamp to [0, 999]. The HTML enforces `min="0"` already, but a hostile
  // user can bypass DOM validation by editing the input or posting /cart/add.js
  // directly — and we'd still compute subtotals / line quantities off those
  // numbers. Clamping keeps downstream math sane even under abuse.
  function num(sel) {
    var v = parseInt(val(sel) || '0', 10);
    if (isNaN(v) || v < 0) return 0;
    if (v > 999) return 999;
    return v;
  }
  function checked(sel) { var el = $(sel); return el ? el.checked : false; }
  function radio(name) { var el = root.querySelector('input[name="' + name + '"]:checked'); return el ? el.value : null; }

  // Reads the Shopify product form's quantity input so multi-keg orders
  // actually get the customer-selected count. Horizon puts the input inside
  // <quantity-input> component inside the product form; any `[name="quantity"]`
  // on the page reliably points to it. Defaults to 1 on any parse failure.
  function readKegQuantity() {
    var input = document.querySelector('product-form-component input[name="quantity"], form[action*="/cart/add"] input[name="quantity"], input[name="quantity"]');
    if (!input) return 1;
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 1) return 1;
    if (v > 999) return 999;
    return v;
  }

  // Look up an add-on by handle. Returns { id, unitPrice } where unitPrice
  // is in DOLLARS (MIKES_ADDONS stores cents per Shopify convention).
  // Throws on missing / null so the caller's try/catch surfaces a visible
  // error to the user instead of silently skipping the line — an admin
  // unpublishing an add-on product used to make checkboxes do nothing.
  function addon(key) {
    var entry = window.MIKES_ADDONS[key];
    if (!entry || entry.id == null) {
      throw new Error('Add-on "' + key + '" is not available right now. Please contact Mike\'s Liquors.');
    }
    return { id: entry.id, unitPrice: (entry.price || 0) / 100 };
  }

  /**
   * Return an array of {variantId, quantity, price, label}. `price` is the
   * LINE total in dollars (unitPrice × quantity), read live from Shopify via
   * window.MIKES_ADDONS — never a hardcoded value. Throws if any selected
   * add-on has been unpublished.
   */
  function buildAddons() {
    var setup = radio('mka-setup') || 'none';
    var lines = [];

    function add(key, qty, label) {
      var a = addon(key);
      lines.push({ variantId: a.id, quantity: qty, price: a.unitPrice * qty, label: label });
    }

    // Cups (always available)
    if (checked('#mka-cups-enable')) {
      var pk = val('#mka-cups') === '100' ? 'cups-16oz-100pk' : 'cups-16oz-50pk';
      add(pk, 1, pk === 'cups-16oz-100pk' ? '100 cups (16oz)' : '50 cups (16oz)');
    }

    if (setup === 'kegerator') {
      var kegGas = readGasPicker('kegerator');
      if (kegGas) lines.push(kegGas);
      var cplr = radio('mka-coupler');
      if (cplr === 'rent' || cplr === 'buy') {
        var key = 'coupler-' + cplr + '-' + couplerHandleKey; // e.g. coupler-rent-type-d
        add(key, 1, (cplr === 'rent' ? 'Rent ' : 'Buy ') + couplerType + ' coupler');
      }
      var faucets = num('#mka-line-cleaning');
      if (faucets > 0) add('line-cleaning-service', faucets, faucets + ' × Line cleaning faucet');
    } else if (setup === 'jockey_box') {
      var size = radio('mka-jockey-size') || 'single';
      var jbKey = size === 'double' ? 'jockey-box-double-rent' : 'jockey-box-single-rent';
      add(jbKey, 1, (size === 'double' ? 'Double' : 'Single') + ' Jockey Box rental');
      var jIce = num('#mka-jockey-ice');
      if (jIce > 0) add('ice-bag', jIce, jIce + ' × ice bag');
      // No gas picker on the jockey-box branch (removed by design — it
      // confused customers). Gas is offered on the Kegerator branch only.
    } else if (setup === 'keg_tub') {
      if (checked('#mka-tub')) add('keg-tub-rental', 1, 'Keg Tub rental');
      if (checked('#mka-pump')) add(couplerHandleKey + '-pump-rental', 1, couplerType + ' pump rental (free)');
      var tIce = num('#mka-tub-ice');
      if (tIce > 0) add('ice-bag', tIce, tIce + ' × ice bag');
    }

    return lines;
  }

  function updateTotal() {
    var el = $('#mka-addons-total');
    if (!el) return;
    try {
      var lines = buildAddons();
      var total = lines.reduce(function (s, l) { return s + l.price; }, 0);
      el.textContent = '$' + total.toFixed(2);
    } catch (err) {
      // An add-on has been unpublished — admin needs to fix it, but the
      // customer shouldn't see NaN. Show a dash and log.
      el.textContent = '—';
      console.warn('[Keg Accessories] updateTotal:', err.message);
    }
  }

  // Recompute total on any input change
  root.addEventListener('change', updateTotal);
  root.addEventListener('input', updateTotal);
  updateTotal();

  // Add to Cart
  $('#mka-add-to-cart').addEventListener('click', function () {
    var btn = $('#mka-add-to-cart');
    var msg = $('#mka-msg');
    msg.textContent = '';
    btn.disabled = true;
    btn.textContent = 'Adding…';

    var setup = radio('mka-setup') || 'none';
    var exchange = num('#mka-exchange');
    var kegQty = readKegQuantity();

    var addons;
    try {
      addons = buildAddons();
    } catch (err) {
      // An add-on product is unpublished/deleted — refuse to proceed.
      msg.textContent = err.message;
      btn.textContent = 'Add to Cart';
      btn.disabled = false;
      console.error('[Keg Accessories]', err);
      return;
    }

    // Build keg's own visible properties so staff sees the choice on the order
    var kegProps = { 'Setup': setupLabel(setup) };
    if (setup === 'kegerator') {
      var cplr = radio('mka-coupler');
      if (cplr === 'have') kegProps['Coupler'] = 'Customer owns ' + couplerType;
      if (cplr === 'rent') kegProps['Coupler'] = 'Renting ' + couplerType;
      if (cplr === 'buy') kegProps['Coupler'] = 'Buying ' + couplerType;
    }

    // Honour the Shopify quantity selector. Add-on quantities are anchored
    // to the original panel inputs (e.g. "5 faucets × line cleaning" is still
    // 5, not 5 × kegQty) — customer can set those per-line.
    var items = [{ id: kegVariantId, quantity: kegQty, properties: kegProps }];
    addons.forEach(function (l) {
      items.push({
        id: l.variantId, quantity: l.quantity,
        properties: { '_For Keg': kegTitle }
      });
      // Atomic ATC: if this addon is a gas refill, include its tank deposit
      // in the SAME /cart/add.js so the cart never enters a "refill present,
      // deposit absent" state. Required for the Cart Validation Function to
      // approve the mutation; deposits.js's polling fallback would also
      // reconcile this, but eagerly bundling avoids the ~3s lag and the
      // 422 from a future-active validator.
      if (l.gasType === 'co2' && window.MIKES_CO2_DEPOSIT_VARIANT) {
        items.push({ id: window.MIKES_CO2_DEPOSIT_VARIANT, quantity: l.quantity });
      } else if (l.gasType === 'n2' && window.MIKES_N2_DEPOSIT_VARIANT) {
        items.push({ id: window.MIKES_N2_DEPOSIT_VARIANT, quantity: l.quantity });
      }
    });
    // Atomic ATC: include the keg deposit in the same /cart/add.js. Same
    // rationale as the gas-deposit injection above. Quantity matches kegQty
    // so multi-keg ATCs (e.g. qty=3) get 3 deposits. Skipped for one-way
    // plastic kegs (Health-Ade, Stumptown) — they take no deposit.
    if (window.MIKES_KEG_DEPOSIT_VARIANT && !kegNoDeposit) {
      items.push({ id: window.MIKES_KEG_DEPOSIT_VARIANT, quantity: kegQty });
    }

    // Post everything in one call so the fetch wrapper sees a SELF mutation
    // (the items array contains deposit variant ids that deposits.js detects).
    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (j) { throw new Error(j.description || j.message || 'Add to cart failed'); });
        return r.json();
      })
      .then(function () {
        // Persist returning-empties count as an ADDITIVE cart attribute. A
        // previous pass would set the value, so adding a second keg with
        // exchange=0 would overwrite and silently lose the earlier count.
        // Now we fetch current, add, then write — so multi-page flows add up.
        if (exchange > 0) {
          return fetch('/cart.js', { credentials: 'same-origin' })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var current = parseInt((cart.attributes || {})['Returning Empty Kegs'] || '0', 10);
              if (isNaN(current)) current = 0;
              var next = current + exchange;
              return fetch('/cart/update.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ attributes: { 'Returning Empty Kegs': String(next) } })
              });
            });
        }
      })
      .then(function () {
        btn.textContent = 'Added ✓';
        // Horizon's cart drawer + cart-items components listen for 'cart:update'
        // (colon, not hyphen — verified in theme/assets/events.js). With no
        // `sections` payload, <cart-items-component> falls back to fetching
        // fresh section HTML via sectionRenderer.renderSection. The deposit
        // added by keg-deposit v2.1 lands a moment later and v2.1's own
        // dispatchCartRefresh fires another cart:update to re-render.
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            sourceId: 'mikes-keg-accessories',
            data: { source: 'mikes-keg-accessories', sections: {} }
          }
        }));
        // Legacy names for any custom listeners:
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart-update', { bubbles: true }));
        setTimeout(function () {
          btn.textContent = 'Add to Cart';
          btn.disabled = false;
        }, 1600);
      })
      .catch(function (e) {
        msg.textContent = 'Error: ' + e.message;
        btn.textContent = 'Add to Cart';
        btn.disabled = false;
        console.error('[Keg Accessories]', e);
      });
  });

  function setupLabel(s) {
    if (s === 'kegerator') return 'Kegerator (customer owns)';
    if (s === 'jockey_box') return 'Jockey Box rental';
    if (s === 'keg_tub') return 'Keg Tub (free rental)';
    return 'No accessories';
  }
})();
